/**
 * What an Ennabl AI impersonation grant points AT — the "Impersonate as" subject.
 *
 * Unlike every other grant kind, an `imp-ai` resource is not a tenant. It is one
 * of three levels, narrowest first:
 *
 *   | level    | resource       | also accepts            | means                |
 *   |----------|----------------|-------------------------|----------------------|
 *   | `email`  | `some@som.com` |                         | that one user        |
 *   | `domain` | `som.com`      | `@som.com`, `*@som.com` | every user @som.com  |
 *   | `all`    | `*`            |                         | every Ennabl AI user |
 *
 * `*@som.com` is accepted because it is exactly how `formatImpAiSubject` PRINTS
 * a domain grant. An operator reading the Grants screen and typing back what
 * they see there must land on the grant they were looking at.
 *
 * Domain matching is EXACT — `som.com` does not cover `mail.som.com`. Subdomains
 * being excluded is a decision, not an oversight: a wildcard that silently grew
 * down the DNS tree would be impossible to audit off the Grants screen.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * MIRROR of `functions/shared/imp-ai-subject.ts` — the UI does not share a TS
 * project with the Lambda, same copy-not-share convention as `grant-view.ts`.
 * The Lambda's copy is the one that validates on write; this one drives the form
 * and the chip labels.
 *
 * ALSO MIRRORED IN PYTHON. The authorization decision is made by ennabl-ai's
 * control-plane (`app/platform/access_ops_grants.py`), so `matches()` exists
 * twice, in two languages. If you change the semantics here, change it there —
 * the two disagreeing means either a grant that does not work or, worse, one
 * that works wider than it reads.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Validation is not cosmetic. The subject is FREE TEXT in the UI, so anything
 * that is not one of the three shapes above produces a grant that can never
 * match anything — it just sits in the table looking granted. A shard uid
 * (`b6e5bf`) and an AWS account id (`306656769105`) are the plausible mistakes,
 * and both are rejected by the domain rule requiring a dot.
 */

export const IMP_AI_ALL = '*';

export type ImpAiSubjectKind = 'all' | 'domain' | 'email';

export interface ImpAiSubject {
    kind: ImpAiSubjectKind;
    /** Normalized value: lowercased, trimmed. `*` for `all`. */
    value: string;
}

// Pragmatic rather than RFC-complete: one `@`, no whitespace, a dot in the
// domain half. The authority on whether an address exists is the target user
// row, not this regex — over-strict validation here would reject real addresses.
//
// `*` is the one character excluded from the local part, and auditing is the
// reason: an address whose local part contains `*` is RFC-legal, held by no real
// mailbox, and reads on the Grants screen as a whole domain while authorizing
// nobody. Refusing it with a message beats issuing a grant that cannot work.
const EMAIL_RE = /^[^\s@*]+@[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;
const DOMAIN_RE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;

// The two ways a domain arrives carrying an `@`: `@som.com` is how people say it
// out loud, and `*@som.com` is how this module prints one — see the docstring.
const DOMAIN_AT_PREFIX_RE = /^\*?@/;

/**
 * Classify a raw subject, or null when it is none of the three shapes.
 *
 * Accepts a leading `@` or `*@` on a domain (`@som.com`, `*@som.com`) because
 * those are how people write "anyone at this domain" — and `*@som.com` is the
 * form this module prints, so the Grants screen round-trips. Silently dropping
 * the grant over one character would be hostile; silently reading it as an
 * address that matches nobody is worse.
 */
export function classifyImpAiSubject(raw: string): ImpAiSubject | null {
    const v = (raw ?? '').trim().toLowerCase();
    if (!v) {
        return null;
    }
    if (v === IMP_AI_ALL) {
        return {kind: 'all', value: IMP_AI_ALL};
    }
    if (v.includes('@')) {
        // `@som.com` / `*@som.com` are a domain written the way people say it out
        // loud, and the way the Grants screen shows it back to them.
        const atPrefix = DOMAIN_AT_PREFIX_RE.exec(v);
        if (atPrefix) {
            const domain = v.slice(atPrefix[0].length);
            return DOMAIN_RE.test(domain) ? {kind: 'domain', value: domain} : null;
        }
        return EMAIL_RE.test(v) ? {kind: 'email', value: v} : null;
    }
    return DOMAIN_RE.test(v) ? {kind: 'domain', value: v} : null;
}

/** Is `raw` a grantable Ennabl AI subject? */
export function isValidImpAiSubject(raw: string): boolean {
    return classifyImpAiSubject(raw) !== null;
}

/**
 * The stored `resource` for a raw subject, or null when invalid. Normalization
 * matters: the grant id is built from this, so `Some@Som.com` and `some@som.com`
 * must not become two rows granting the same thing.
 */
export function normalizeImpAiSubject(raw: string): string | null {
    return classifyImpAiSubject(raw)?.value ?? null;
}

/**
 * Does a stored subject authorize impersonating `targetEmail`?
 *
 * The reference implementation of the matching rule. Kept in sync with the
 * Python copy in ennabl-ai — see the module docstring.
 */
export function matchesImpAiSubject(storedResource: string, targetEmail: string): boolean {
    const subject = classifyImpAiSubject(storedResource);
    const target = (targetEmail ?? '').trim().toLowerCase();
    if (!subject || !target || !target.includes('@')) {
        return false;
    }
    switch (subject.kind) {
        case 'all':
            return true;
        case 'email':
            return subject.value === target;
        case 'domain':
            // Exact domain, no subdomains — see the module docstring.
            return target.slice(target.lastIndexOf('@') + 1) === subject.value;
    }
}

/**
 * How a subject reads on the Grants screen. The LEVEL has to be obvious at a
 * glance: an auditor scanning that column is answering "who can log in as whom",
 * and a bare `som.com` next to a bare `a@som.com` does not say which is broader.
 */
export function formatImpAiSubject(storedResource: string): string {
    const subject = classifyImpAiSubject(storedResource);
    if (!subject) {
        return storedResource;
    }
    switch (subject.kind) {
        case 'all':
            return 'ALL';
        case 'domain':
            return `*@${subject.value}`;
        case 'email':
            return subject.value;
    }
}

/** Human-readable error for a rejected subject, for form and API messages. */
export function impAiSubjectError(raw: string): string | null {
    if (isValidImpAiSubject(raw)) {
        return null;
    }
    return `"${raw}" is not a valid subject — use an email (a@b.com), a domain (b.com or *@b.com), or * for everyone`;
}
